/**
 * WHAT ALREADY WENT OUT — the sent half of the reminder screen (U2 / 5.6b).
 *
 * ===========================================================================
 * ⚠ A TABLE HERE, AND A CARD IN THE QUEUE. THE SHAPE FOLLOWS THE CONTENT
 * ===========================================================================
 * `RenewalDraftsPage`'s queue is cards because its central content is a multi-line LETTER
 * plus a recipient plus a refusal reason. A history row has none of those: every column is
 * a short scalar — who, what, which window, which expiry the letter stated, which address,
 * when. That is the register's shape (U5.5), so it takes the register's shape.
 *
 * ===========================================================================
 * ⚠ NO LETTER, AND NO MONEY. NEITHER IS AN OVERSIGHT
 * ===========================================================================
 * The API returns neither, for every seat. `buildCustomerReminderMail` is PURE and
 * re-renders from CURRENT facts, so re-drawing the letter under a heading that says "sent"
 * would present today's text as the one delivered — and product, party and contact names are
 * all live joins, so it really would differ. `line_total` is live too: a contract can be
 * re-priced after a reminder goes out. Two things are snapshotted in the ledger and only
 * two, `recipient_email` and `expiry_date`, and this screen labels them as such.
 *
 * ⚠ A MISSING NAME IS SAID, NOT BLANK. `party_name`, `product_name` and `recipient_name`
 * are nullable because they are LIVE joins and the ledger has no foreign key on `party_id`
 * or `product_id` (measured against the live DDL), while `recipient_contact_id` is
 * `ON DELETE SET NULL`. A blank cell there reads as "this column did not load"; the row
 * still proves a letter went out, so it says so in words and keeps its place.
 *
 * ===========================================================================
 * ⚠ COLUMN HEADERS ARE `text-fg`, AND THAT IS A MEASUREMENT
 * ===========================================================================
 * This `<tr>` sets its own `bg-subtle`, where BOTH of the app's dimmer text tokens fail AA
 * in light mode — `text/muted` 2.49:1 and `text/secondary` 4.40:1 — against a 4.5 floor for
 * 11px/500 labels, which have no large-text excuse. `text/primary` on that tint is 16.14:1.
 * The register was corrected to exactly this in the same phase and for exactly this reason;
 * copying its OLD recipe would have re-introduced a defect that has just been paid for.
 */

import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { NO_VALUE } from '../inquiries/format';
import { formatDateOnly, formatSentAt } from './format';
import { PagerFooter } from './PagerFooter';
import { draftsErrorText } from './drafts-api';
import type { RenewalSentHistoryPage, RenewalSentRow } from './drafts-api';

export function SentReminders({
  countryCode,
  page,
  isPending,
  isError,
  error,
  onRetry,
  onPage,
  partyFilter,
  onClearParty,
}: {
  countryCode: string;
  page: RenewalSentHistoryPage | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  onPage: (next: number) => void;
  /** The party this list is narrowed to, or null. Drives the copy, never inferred. */
  partyFilter: string | null;
  onClearParty: () => void;
}): React.JSX.Element {
  if (isPending) {
    return (
      <div className="rounded-md border border-line bg-surface px-4">
        <SkeletonRows rows={4} label="Loading sent reminders" />
      </div>
    );
  }
  if (isError || page === undefined) {
    return (
      <Alert tone="danger" title="The sent reminders could not be loaded">
        <p>{draftsErrorText(error)}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 font-medium underline underline-offset-2"
        >
          Try again.
        </button>
      </Alert>
    );
  }

  const rows = page.data;

  /*
   * ⚠ AN EMPTY PAGE IS NOT AN EMPTY HISTORY — the U5.5 defect, on the screen where the
   * consequence is somebody concluding a customer has never been contacted.
   */
  if (rows.length === 0 && page.total > 0) {
    return (
      <Alert tone="info" title={`Page ${String(page.page)} is past the end of this list`}>
        <p>
          {String(page.total)} {page.total === 1 ? 'reminder has' : 'reminders have'} been sent.
        </p>
        <button
          type="button"
          onClick={() => onPage(1)}
          className="mt-1 font-medium underline underline-offset-2"
        >
          Show the first page.
        </button>
      </Alert>
    );
  }

  if (rows.length === 0) {
    /*
     * ⚠ TWO DIFFERENT EMPTY STATES, BECAUSE THEY ARE TWO DIFFERENT FACTS. "Nobody in this
     * country has ever been sent one" and "this customer has never been sent one" lead to
     * opposite actions, and a shared sentence would let a filtered view be read as the
     * whole country's. The filtered one offers the way back out.
     */
    return (
      <div className="rounded-md border border-dashed border-line bg-surface">
        {partyFilter === null ? (
          <EmptyState
            title="No customer renewal reminder has been sent in this country yet."
            description="This list holds reminders a person reviewed and sent. Drafts waiting for that review are on the other tab."
          />
        ) : (
          <EmptyState
            title="This customer has not been sent a renewal reminder."
            description="Nothing has gone out to this customer from this system. Other customers may have been contacted — this list is filtered."
            action={
              <button
                type="button"
                onClick={onClearParty}
                className="text-body13 font-medium text-indigo hover:underline"
                data-testid="clear-party-filter"
              >
                Show everything sent in {countryCode}.
              </button>
            }
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-md border border-line bg-surface">
        <table className="w-full min-w-[900px] border-collapse text-left" data-testid="sent-table">
          <thead>
            <tr className="h-10 border-b border-line bg-subtle">
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Sent
              </th>
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Customer
              </th>
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Product
              </th>
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Notice
              </th>
              {/*
                ⚠ "EXPIRY STATED" AND NOT "EXPIRES". The ledger snapshots the date the LETTER
                carried; the contract's expiry today may be a different date entirely — that
                is exactly the edit that re-arms the windows. A column headed "Expires" here
                would be a live fact under a historical heading.
              */}
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Expiry stated
              </th>
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Sent to
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <SentRowCells key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      </div>
      <PagerFooter
        page={page.page}
        perPage={page.perPage}
        shown={rows.length}
        total={page.total}
        onPage={onPage}
      />
    </>
  );
}

function SentRowCells({ row }: { row: RenewalSentRow }): React.JSX.Element {
  return (
    <tr
      data-testid="sent-row"
      data-sent-id={row.id}
      data-source={row.source}
      className="crf-register-row border-b border-line last:border-b-0"
    >
      <td className="tnum px-3 py-2 text-body13 text-fg">
        {formatSentAt(row.sent_at)}
      </td>
      {/*
        ⚠ TEXT, NOT A LINK — RULE-7 CALLOUT, THIS CELL USED TO BE A `<Link>` TO A ROUTE THAT
        DOES NOT EXIST. A completeness review ran `matchRoutes` against the real exported
        route table: `/countries/IN/parties/77` matches `['(layout)','/countries/:cc',
        '(layout)','*']` — the Not-found catch-all. `domain/nav.ts:324` marks the parties
        entry `available: false` and no feature mounts the path.
        `renewal-drafts.test.tsx` asserted that href, which pinned the dead link in place;
        that case is updated in the same change.

        This repo forbids it explicitly, twice, in files this feature touches:
        `WorkOrderRecordPage` — "A link styled as one that 404s is worse than the sentence
        below it"; `DraftCard` — "a dead link ... is worse than no link because the operator
        concludes the document is missing". On the ONE screen built to prove a letter reached
        a customer, sending them to Not found is the worst possible place for it.

        ⚠ AND IT ALSO CLOSES A MEASURED CONTRAST FAILURE. `text-indigo` on `bg-surface` is
        4.22:1 light / 3.90:1 dark at 13px/500 — under the 4.5 floor in BOTH modes, with no
        large-text excuse. That pair is an app-wide idiom the shell already carries
        (`quotes.contrast.test.ts` records it as a SHELL-WIDE finding), so it is not this
        screen's to re-decide — but a cell that should not be a link anyway does not get to
        keep a failing pair on a technicality. `text-fg` here is 17.04 / 15.11.

        The two OTHER dead party links in this feature (`RenewalsPage.tsx` and
        `RenewalDraftsPage.tsx`'s card) PRE-DATE this work and are FILED, not folded in.
      */}
      <td className="px-3 py-2 text-body13 font-medium text-fg">
        {row.party_name ?? 'Customer record removed'}
      </td>
      <td className="px-3 py-2 text-body13 text-fg">
        {row.product_name ?? 'Product record removed'}
      </td>
      <td className="px-3 py-2 text-body13 text-fg">{row.window_days}-day</td>
      <td className="tnum px-3 py-2 text-body13 text-fg">{formatDateOnly(row.expiry_date)}</td>
      {/*
        ⚠ THE ADDRESS IS `text-fg` AT BODY SIZE, NOT A MUTED CAPTION. It is the only fact on
        this row that nothing else can re-derive — the ledger snapshots it precisely so
        "where did it actually go" stays answerable after the contact row changes or is
        deleted. The live contact NAME sits beside it and may be absent; the address may not.
      */}
      <td className="px-3 py-2 text-body13 text-fg" data-testid="sent-recipient">
        {row.recipient_email ?? NO_VALUE}
        {row.recipient_name === null ? null : (
          <span className="text-fg-secondary"> · {row.recipient_name}</span>
        )}
      </td>
    </tr>
  );
}
